import { Component, ChangeDetectionStrategy, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { AuthProfile } from './models/auth.model';

@Component({
  selector: 'app-country-card',
  imports: [CommonModule, RouterModule, MatIconModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div
      [id]="'country-card-' + account().uid"
      class="group relative overflow-hidden flex items-center gap-4 p-4 rounded-[24px] cursor-pointer border border-white/40 dark:border-white/10 bg-white/40 dark:bg-black/40 backdrop-blur-2xl shadow-[0_8px_32px_0_rgba(0,0,0,0.04)] dark:shadow-[0_8px_32px_0_rgba(0,0,0,0.3)] hover:shadow-[0_12px_40px_0_rgba(0,0,0,0.08)] dark:hover:shadow-[0_12px_40px_0_rgba(0,0,0,0.5)] hover:scale-[1.02] active:scale-[0.98] transition-all duration-300"
      [routerLink]="['/profile', account().uid]"
      (click)="cardClick.emit(account())"
      tabindex="0"
    >
      <!-- Premium background liquid glass gradient overlay -->
      <div
        class="absolute inset-0 bg-gradient-to-tr from-emerald-500/5 via-transparent to-ios-blue/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
      ></div>

      <!-- Avatar/Flag Container: Perfectly rectangular with sharp corners, no rounded edges -->
      <div
        [id]="'country-logo-container-' + account().uid"
        class="w-16 h-11 rounded-none overflow-hidden bg-slate-100 dark:bg-zinc-800 shrink-0 border border-black/10 dark:border-white/15 relative z-10 shadow-sm flex items-center justify-center"
      >
        @if (account().photoURL) {
          <img
            [src]="account().photoURL"
            [alt]="account().displayName"
            class="w-full h-full object-cover rounded-none transition-transform duration-500 group-hover:scale-105"
            referrerpolicy="no-referrer"
          />
        } @else {
          <mat-icon
            [id]="'country-fallback-icon-' + account().uid"
            class="text-emerald-600 dark:text-emerald-400 text-2xl !w-6 !h-6 flex items-center justify-center font-bold"
            >public</mat-icon
          >
        }
      </div>

      <!-- Country Info Block -->
      <div class="flex-1 min-w-0 relative z-10">
        <div class="flex items-center gap-1.5 min-w-0">
          <h4
            [id]="'country-name-' + account().uid"
            class="text-base font-bold text-gray-900 dark:text-white group-hover:text-ios-blue transition-colors duration-300 leading-tight"
          >
            {{ (account().displayName || "").charAt(0).toUpperCase() + (account().displayName || "").substring(1).toLowerCase() }}
          </h4>
          <mat-icon
            [id]="'country-verified-icon-' + account().uid"
            class="text-ios-blue !text-[16px] !w-4 !h-4 flex items-center justify-center shrink-0"
            title="Verified Country"
            >verified</mat-icon
          >
        </div>
        <div class="flex items-center gap-2 mt-0.5">
          <p
            [id]="'country-username-' + account().uid"
            class="text-xs font-medium text-gray-500 dark:text-zinc-400"
          >
            {{ account().username ? "@" + account().username?.toLowerCase() : "@country" }}
          </p>
          @if (account().countryCode) {
            <span
              class="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-md bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
            >
              {{ account().countryCode }}
            </span>
          }
        </div>
      </div>

      <!-- Arrow Action Indicator -->
      <div class="relative z-10 shrink-0 flex items-center justify-center">
        <mat-icon
          [id]="'country-arrow-' + account().uid"
          class="text-gray-400 dark:text-zinc-500 group-hover:text-emerald-500 opacity-0 group-hover:opacity-100 transition-all -translate-x-2 group-hover:translate-x-0 duration-300 !text-[20px] !w-[20px] !h-[20px]"
          >chevron_right</mat-icon
        >
      </div>
    </div>
  `
})
export class CountryCard {
  account = input.required<AuthProfile>();
  cardClick = output<AuthProfile>();
}
